import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property} from "lit/decorators.js";
import {UnLitElement} from "../../common/elements";
import {resolve} from "../../container";
import {Dictionary} from "./dictionary";
import {TrackingEventName} from "../../tracking/types";
import {ScrollBarFactory, type ScrollButtonGroup} from "./scrollBarFactory";

export type ScrollDirection =
    | {readonly value: "left"; readonly iconId: "left_arrow"}
    | {readonly value: "right"; readonly iconId: "right_arrow"};

abstract class EopScrollButton extends UnLitElement {
    public abstract readonly direction: ScrollDirection;

    @property({type: Boolean, reflect: true})
    public active: boolean = false;

    protected scrollGroup?: ScrollButtonGroup;
    private groupParent?: HTMLElement;

    private dictionary: Dictionary;

    public constructor(protected readonly scrollBarFactory: ScrollBarFactory = resolve(ScrollBarFactory)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        const parent = this.parentElement;
        if (!parent) {
            throw new Error("Scroll buttons require a parent element");
        }
        this.scrollGroup = this.register(parent);
        this.groupParent = parent;
    }

    public disconnectedCallback(): void {
        if (this.groupParent) {
            this.unregister(this.groupParent);
        }
        this.scrollGroup = undefined;
        this.groupParent = undefined;
        this.active = false;
        super.disconnectedCallback();
    }

    public render(): TemplateResult {
        return html`
            <button type="button" class="scroll-button scroll-${this.direction.value}"
                    data-tracking-event-name=${TrackingEventName.NAVIGATION_INTERACT}
                    data-tracking-payload-text=${`scroll-${this.direction.value}`}
                    aria-label=${this.dictionary.translate(this.direction.value === "left" ? "MSG_SCROLL_LEFT" : "MSG_SCROLL_RIGHT")}
                    tabindex="-1" @click=${this.handleClick}>
                <eop-svg-sprite-icon icon-id=${this.direction.iconId}></eop-svg-sprite-icon>
            </button>
        `;
    }

    public buttonWidth(): number {
        return this.querySelector("button")?.clientWidth ?? 0;
    }

    protected abstract register(parent: HTMLElement): ScrollButtonGroup;

    protected abstract unregister(parent: HTMLElement): void;

    protected abstract handleClick(): void;

}

@customElement("eop-scroll-button-left")
export class EopScrollButtonLeft extends EopScrollButton {
    public readonly direction: ScrollDirection = {value: "left", iconId: "left_arrow"};

    public innerEdge(): number {
        return this.querySelector("button")?.rightOffset() ?? this.rightOffset();
    }

    protected handleClick(): void {
        this.scrollGroup?.scrollLeft();
    }

    protected register(parent: HTMLElement): ScrollButtonGroup {
        return this.scrollBarFactory.registerLeft(parent, this);
    }

    protected unregister(parent: HTMLElement): void {
        this.scrollBarFactory.unregisterLeft(parent, this);
    }
}

@customElement("eop-scroll-button-right")
export class EopScrollButtonRight extends EopScrollButton {
    public readonly direction: ScrollDirection = {value: "right", iconId: "right_arrow"};

    public innerEdge(): number {
        return this.querySelector("button")?.leftOffset() ?? this.leftOffset();
    }

    protected handleClick(): void {
        this.scrollGroup?.scrollRight();
    }

    protected register(parent: HTMLElement): ScrollButtonGroup {
        return this.scrollBarFactory.registerRight(parent, this);
    }

    protected unregister(parent: HTMLElement): void {
        this.scrollBarFactory.unregisterRight(parent, this);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.scrollGroup?.scrollToInitialTarget();
    }
}
